"use client";

import { useQuery } from "@tanstack/react-query";
import { Button } from "@workspace/ui/components/button";
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@workspace/ui/components/responsive-dialog";
import { useIsMobile } from "@workspace/ui/hooks/use-mobile";
import { getCookie } from "cookies-next/client";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { LoginOptions } from "@/components/auth/login-options";
import {
  getNextAuthSignInErrorMessage,
  OAUTH_ACCOUNT_NOT_LINKED_ERROR,
} from "@/lib/auth/auth-error-codes";
import {
  getClientPostAuthDestination,
  getFirstQueryParam,
} from "@/lib/auth/callback-url";
import {
  PREFERRED_SIGN_IN_PROVIDER_COOKIE,
  parsePreferredSignInProvider,
  setPreferredSignInProvider,
} from "@/lib/auth/preferred-sign-in-provider";
import { resolveLoginProviders } from "@/lib/auth/resolve-login-providers";
import { queryKeys } from "@/lib/query-keys";
import type { UserLoginValues } from "@/lib/schemas/login";

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  returnTo?: string;
  preferDialog?: boolean;
};

async function fetchQaLoginEnabled() {
  const response = await fetch("/api/auth/qa-login/enabled");
  const data = (await response.json()) as { enabled?: boolean };
  return data.enabled === true;
}

function AuthErrorMessage({ error }: { error: string | undefined }) {
  const authErrorMessage = getNextAuthSignInErrorMessage(error);
  const isOAuthAccountNotLinked = error === OAUTH_ACCOUNT_NOT_LINKED_ERROR;

  if (!authErrorMessage) {
    return null;
  }

  if (isOAuthAccountNotLinked) {
    return (
      <p className="rounded-md border border-score-amber/30 bg-score-amber-soft p-3 text-score-amber text-xs">
        {authErrorMessage}
      </p>
    );
  }

  return (
    <p className="rounded-md border border-destructive/30 bg-score-red-soft p-3 text-destructive text-xs">
      {authErrorMessage}
    </p>
  );
}

export function LoginModal({
  isOpen,
  onOpenChange,
  returnTo,
  preferDialog = false,
}: Props) {
  const router = useRouter();
  const isMobile = useIsMobile();
  const showCancelFooter = isMobile && !preferDialog;

  const error = getFirstQueryParam(router.query.error);

  const [loading, setLoading] = useState({
    credentials: false,
    google: false,
  });

  const preferredProvider = useMemo(() => {
    if (!isOpen) {
      return null;
    }

    const cookieValue = getCookie(PREFERRED_SIGN_IN_PROVIDER_COOKIE);
    return parsePreferredSignInProvider(
      typeof cookieValue === "string" ? cookieValue : null
    );
  }, [isOpen]);

  const { data: qaLoginEnabled = false } = useQuery({
    queryKey: queryKeys.auth.qaLoginEnabled,
    queryFn: fetchQaLoginEnabled,
    enabled: isOpen,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const { primary, accordion } = resolveLoginProviders({
    preferred: preferredProvider,
    qaLoginEnabled,
  });

  const onGoogleLogin = useCallback(async () => {
    const postAuthDestination = getClientPostAuthDestination({
      explicitReturnTo: returnTo,
    });

    setLoading((value) => ({
      ...value,
      google: true,
    }));

    try {
      setPreferredSignInProvider("google");
      await signIn("google", { redirectTo: postAuthDestination });
    } catch (_e) {
      toast.error("Uh oh! Something went wrong.", {
        description: "Failed to login with Google. Try again",
      });
      setLoading((value) => ({
        ...value,
        google: false,
      }));
    }
  }, [returnTo]);

  const onSubmit = useCallback(
    async ({ email }: UserLoginValues) => {
      const postAuthDestination = getClientPostAuthDestination({
        explicitReturnTo: returnTo,
      });

      setLoading((value) => ({
        ...value,
        credentials: true,
      }));

      try {
        setPreferredSignInProvider("email");
        const res = await signIn("email", {
          redirectTo: postAuthDestination,
          email: email.toLowerCase(),
          redirect: false,
        });
        if (res?.error || res?.url?.includes("error")) {
          const message =
            getNextAuthSignInErrorMessage(res?.error) ??
            "Failed to login with email. Try again";
          toast.error("Uh oh! Something went wrong.", {
            description: message,
          });
          return;
        }
        toast.success("Check your email", {
          description:
            "We sent you a login link. Be sure to check your spam too.",
        });
        onOpenChange(false);
      } catch (_e) {
        toast.error("Uh oh! Something went wrong.", {
          description: "Failed to login with email. Try again",
        });
      } finally {
        setLoading((value) => ({
          ...value,
          credentials: false,
        }));
      }
    },
    [onOpenChange, returnTo]
  );

  const isLoading = loading.credentials || loading.google;

  if (!isOpen) {
    return null;
  }

  const loginBody = (
    <div className="space-y-4">
      <AuthErrorMessage error={error} />
      <LoginOptions
        accordion={accordion}
        primary={primary}
        providerProps={{
          credentialsLoading: loading.credentials,
          disabled: isLoading,
          emailFormId: "auth/login",
          emailSubmitLabel: "Continue",
          googleLoading: loading.google,
          onEmailSubmit: onSubmit,
          onGoogleLogin,
        }}
      />
    </div>
  );

  return (
    <ResponsiveDialog
      forceDialog={preferDialog}
      onOpenChange={onOpenChange}
      open={isOpen}
    >
      <ResponsiveDialogContent className="sm:max-w-lg">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Login to VacWork</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            Sign in or create an account
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        {loginBody}
        {showCancelFooter ? (
          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
          </ResponsiveDialogFooter>
        ) : null}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
